import React from "react";
import { Building2, LayoutDashboard, Newspaper, Zap } from "lucide-react";
import { AppLink } from "../ui/link";
import { cn } from "../../lib/utils";
import type { AdminRouteMap } from "../../pages/AdminAppPage/types";
import { mobileScrollLinkClass } from "./navLinkStyles";

type AdminNavVariant = "sidebar" | "mobileMenu";

type AdminNavLinksProps = {
  routes: AdminRouteMap;
  currentPath: string;
  variant: AdminNavVariant;
  onNavigate?: () => void;
};

const ADMIN_NAV_ITEMS = [
  {
    id: "overview",
    label: "Overview",
    pathKey: "admin" as const,
    icon: LayoutDashboard,
    isActive: (currentPath: string, routes: AdminRouteMap) =>
      currentPath === routes.admin || currentPath === `${routes.admin}/`,
  },
  {
    id: "organizations",
    label: "Organizations",
    pathKey: "adminOrganizations" as const,
    icon: Building2,
    isActive: (currentPath: string, routes: AdminRouteMap) =>
      currentPath === routes.adminOrganizations ||
      currentPath.startsWith(`${routes.adminOrganizations}/`) ||
      currentPath === routes.adminUsers ||
      currentPath.startsWith(`${routes.adminUsers}/`) ||
      currentPath === routes.adminBusinesses ||
      currentPath.startsWith(`${routes.adminBusinesses}/`),
  },
  {
    id: "blog",
    label: "Blog",
    pathKey: "adminBlogPosts" as const,
    icon: Newspaper,
    isActive: (currentPath: string, routes: AdminRouteMap) =>
      currentPath === routes.adminBlogPosts ||
      currentPath.startsWith(`${routes.adminBlogPosts}/`),
  },
  {
    id: "jobs",
    label: "Jobs",
    pathKey: "adminJobs" as const,
    icon: Zap,
    isActive: (currentPath: string, routes: AdminRouteMap) =>
      currentPath === routes.adminJobs,
  },
] as const;

const sidebarLinkClass = (isActive: boolean) =>
  cn(
    "no-underline hover:no-underline flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border",
    isActive
      ? "bg-premium-50 text-premium-600 shadow-sm border-premium-100/50 hover:text-premium-600"
      : "text-muted-foreground hover:bg-muted/50 hover:text-foreground border-transparent",
  );

const mobileLinkClass = (isActive: boolean) =>
  cn(
    mobileScrollLinkClass,
    "flex items-center gap-3",
    isActive && "bg-premium-50/70 text-premium-700",
  );

export const AdminNavLinks: React.FC<AdminNavLinksProps> = ({
  routes,
  currentPath,
  variant,
  onNavigate,
}) => {
  const linkClass = variant === "sidebar" ? sidebarLinkClass : mobileLinkClass;

  return (
    <>
      {variant === "sidebar" && (
        <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-3 px-3">
          Admin
        </div>
      )}
      {ADMIN_NAV_ITEMS.map((item) => {
        const isActive = item.isActive(currentPath, routes);
        const Icon = item.icon;
        return (
          <AppLink
            key={item.id}
            to={routes[item.pathKey]}
            variant="muted"
            className={linkClass(isActive)}
            onClick={onNavigate}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {item.label}
          </AppLink>
        );
      })}
    </>
  );
};
